/**
 * Why the identity provider leg of an SSO sign-in ended without a session.
 *
 * `notAuthorized` — the identity provider (or Cognito, on its behalf) refused
 * the sign-in. In practice this is almost always the customer's own directory
 * not having this account enabled for Sytex: at Claro, for instance, a person
 * can hold two or three addresses and only one of them is the one their IdP
 * enabled (SYT-13549). It is not a Sytex failure, so the message has to send
 * the user to their own administrator.
 *
 * `temporarilyUnavailable` — the provider or Cognito answered with a
 * server-side error. Retrying is the right advice here, and it is the only
 * family where blaming the account would be wrong.
 */
export type SsoLoginFailureKind = 'notAuthorized' | 'temporarilyUnavailable';

export class SsoLoginFailure {
  kind: SsoLoginFailureKind;

  /**
   * Cognito's raw `error_description`, verbatim. AWS documents these as free
   * text that may change, so nothing branches on it — it is carried only so
   * the screen can show support what the provider actually said.
   */
  detail?: string;

  constructor({ kind, detail }: SsoLoginFailureData) {
    this.kind = kind;
    this.detail = detail;
  }
}

export interface SsoLoginFailureData {
  kind: SsoLoginFailureKind;
  detail?: string;
}

const TEMPORARY_ERROR_CODES = ['server_error', 'temporarily_unavailable'];

/**
 * Reads the OAuth error Cognito appends to the callback URL
 * (`…/callback?error=<code>&error_description=<text>`) and turns it into the
 * failure the screen has to explain.
 *
 * Returns `null` when there is no `error` param, which is the normal case:
 * either a successful return carrying a `code`, or a plain visit to the login
 * page.
 *
 * Only the fixed OAuth 2.0 error code is matched. The description is free text
 * that AWS explicitly warns against pattern-matching, and for a SAML provider
 * it does not even carry the provider's own message, so anything that is not a
 * documented server-side failure is treated as a refusal.
 */
export function ssoLoginFailureFromCallback(
  error: string | null,
  errorDescription: string | null
): SsoLoginFailure | null {
  if (!error) {
    return null;
  }

  const kind: SsoLoginFailureKind = TEMPORARY_ERROR_CODES.includes(error.toLowerCase())
    ? 'temporarilyUnavailable'
    : 'notAuthorized';

  return new SsoLoginFailure({ kind, detail: errorDescription ?? undefined });
}
